import React from 'react';
import { Grid, Typography, FormControl, Select, MenuItem, TextField, InputAdornment, IconButton, Box, Tooltip } from '@material-ui/core';
import { Search, Clear, Cached } from '@material-ui/icons';
import clsx from 'clsx';
import moment from 'moment';
import { SEARCH_CRITERIA_OPTIONS, ROWS_PER_PAGE_OPTIONS } from '../utils/constants';

// Criteria whose value is a date — rendered with a date-picker input. The
// native date input already emits "YYYY-MM-DD" (e.g. "2026-07-22"), which is
// exactly the format the backend expects, so searchValue is stored as-is.
const DATE_SEARCH_CRITERIA = ['startDate', 'expectedEndDate'];

const SearchSection = ({
  classes,
  searchCriteria,
  setSearchCriteria,
  searchValue,
  setSearchValue,
  handleSearch,
  handleClearSearch,
  lastSyncTime,
  handleRefresh,
  rowsPerPage,
  handleRowsPerPageChange,
}) => {
  const isDateCriteria = DATE_SEARCH_CRITERIA.includes(searchCriteria);

  return (
    <Grid
      container
      style={{
        display: 'flex',
        width: '100%',
        alignItems: 'end',
        flexWrap: 'wrap',
        marginTop: '1rem',
        overflowX: 'hidden',
      }}
    >
      <Grid
        item
        xs={12}
        sm={4}
        md={4}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
          justifyContent: 'flex-start',
        }}
      >
        <Grid>
          <Typography
            variant="body2"
            style={{
              fontWeight: 600,
              whiteSpace: 'nowrap',
              marginBottom: '1.25rem',
              fontSize: '0.875rem',
            }}
            className="dashboard-filter-labels"
          >
            Search by:
          </Typography>
        </Grid>
        <Grid
          item
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'stretch',
              height: '40px',
            }}
            className="select-search-by-field"
          >
            <FormControl
              variant="outlined"
              size="small"
              className={classes.searchSelect}
              style={{
                backgroundColor: '#F0F0F0',
                minWidth: '140px',
              }}
            >
              <Select
                value={searchCriteria}
                onChange={(e) => {
                  setSearchCriteria(e.target.value);
                  // Clear the value so a leftover text value doesn't linger in
                  // (or invalidate) the date input when switching criteria.
                  setSearchValue('');
                }}
                style={{ outline: 'none' }}
                onFocus={(e) => e.target.style.outline = 'none'}
              >
                {SEARCH_CRITERIA_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              type={isDateCriteria ? 'date' : 'text'}
              value={searchValue}
              disabled={searchCriteria === 'all'}
              onChange={(e) => {
                if (searchCriteria !== 'all') {
                  // For date criteria the native input already gives
                  // "YYYY-MM-DD" (e.g. "2026-07-22"); store it directly.
                  setSearchValue(e.target.value);
                }
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              onFocus={(e) => e.target.style.outline = 'none'}
              variant="outlined"
              placeholder={isDateCriteria ? undefined : 'Search'}
              className={classes.searchInput}
              style={{
                flex: 1,
                minWidth: 0,
              }}
              InputProps={{
                style: {
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  paddingRight: searchCriteria !== 'all' ? '8px' : '0px',
                  height: '100%',
                },
                // Cross/cancel icon shown for every selected criterion (not
                // just when a value is typed). Clicking it clears both the
                // criteria (key) and the value and refreshes the list.
                endAdornment: searchCriteria !== 'all' ? (
                  <InputAdornment position="end">
                    <Tooltip title="Clear search" aria-label="clear">
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        style={{
                          padding: '4px',
                          marginRight: '4px',
                        }}
                      >
                        <Clear
                          fontSize="small"
                          style={{
                            cursor: 'pointer',
                            color: 'dimgray',
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              }}
            />
            <IconButton
              onClick={handleSearch}
              disabled={searchCriteria === 'all'}
              style={{
                backgroundColor: '#EB5E28',
                color: 'white',
                borderRadius: 0,
                borderTopRightRadius: '4px',
                borderBottomRightRadius: '4px',
                border: '1px solid #000',
                borderLeft: 'none',
                padding: '8px 12px',
                height: '100%',
                minWidth: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={(e) => {
                if (searchCriteria !== 'all') {
                  e.currentTarget.style.backgroundColor = '#d35400';
                }
              }}
              onMouseLeave={(e) => {
                if (searchCriteria !== 'all') {
                  e.currentTarget.style.backgroundColor = '#EB5E28';
                }
              }}
            >
              <Search style={{ fontSize: '20px' }} />
            </IconButton>
          </Box>
        </Grid>
      </Grid>

      <Grid
        item
        xs={12}
        sm={8}
        md={8}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          justifyContent: 'flex-start',
        }}
      >
        <Grid
          container
          className={classes.refreshContainerSty}
          justifyContent="flex-end"
          style={{ marginBottom: '2px' }}
        >
          <Typography variant="caption" className={clsx(classes.padR20)}>
            {`Last synced @ ${moment(lastSyncTime).format('DD-MMM-YYYY HH:mm:ss')}`}
          </Typography>
          <Typography
            variant="caption"
            color="primary"
            className={classes.refreshSty}
            onClick={handleRefresh}
          >
            Refresh
            <Cached color="primary" style={{ fontSize: 15 }} />
          </Typography>
        </Grid>
        <Grid container justifyContent="flex-end" alignItems="center">
          <Typography variant="body2" className={classes.hgText}>
            PER PAGE
          </Typography>
          <FormControl className={classes.formPerPage}>
            <Select
              labelId="perpage-filter"
              id="perpage-filter-id"
              value={rowsPerPage}
              variant="outlined"
              onChange={handleRowsPerPageChange}
              style={{ outline: 'none' }}
              onFocus={(e) => e.target.style.outline = 'none'}
              classes={{
                outlined: classes.outlined,
              }}
            >
              {ROWS_PER_PAGE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SearchSection;

